import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { HelpCircle, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getHelpForPath } from '@/data/helpContent';

export const HelpButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const help = getHelpForPath(pathname);

  // Temporarily remove transform from sidebar so overlay covers it properly
  useEffect(() => {
    if (open) {
      const sidebar = document.querySelector('aside[class*="bg-macartur-accent1"]') as HTMLElement;
      if (sidebar) {
        sidebar.style.setProperty('transform', 'none', 'important');
      }
      return () => {
        if (sidebar) {
          sidebar.style.removeProperty('transform');
        }
      };
    }
  }, [open]);

  if (!help) return null;

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setOpen(true)}
        className="text-gray-500 hover:text-macartur-primary"
        title="Ayuda"
      >
        <HelpCircle className="h-5 w-5" />
      </Button>

      {open && createPortal(
        <>
          <div
            className="fixed inset-0 bg-black/80"
            style={{ zIndex: 9998 }}
            onClick={() => setOpen(false)}
          />

          <div
            className="fixed inset-y-0 right-0 w-[400px] sm:w-[440px] p-6 overflow-y-auto bg-white shadow-xl animate-in slide-in-from-right duration-300"
            style={{ zIndex: 9999 }}
          >
            <button
              onClick={() => setOpen(false)}
              className="absolute right-4 top-4 rounded-sm opacity-70 hover:opacity-100"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="mb-6">
              <h2 className="text-xl font-semibold">{help.title}</h2>
              <p className="text-sm text-gray-500 mt-1">{help.description}</p>
            </div>

            <div className="space-y-4">
              {help.steps.map((step, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center text-sm font-semibold mt-0.5">
                    {index + 1}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-sm font-semibold text-gray-900">{step.title}</h4>
                    <p className="text-sm text-gray-600 mt-0.5 leading-relaxed">{step.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>,
        document.body
      )}
    </>
  );
};
